import React from "react"
import styles from "./CircularProgress.module.scss"

export interface CircularProgressProps {
  /** Progress value as a percentage (0-100) */
  value: number
  /** Size of the circle in pixels */
  size?: number
  /** Thickness of the progress ring */
  strokeWidth?: number
  /** Color of the progress ring */
  progressColor?: string
  /** Color of the background ring */
  backgroundColor?: string
  /** Color of the inner circle */
  innerBackgroundColor?: string
  /** Text color for the percentage display */
  textColor?: string
  /** Font size for the percentage text */
  fontSize?: number
  /** Whether to show the percentage text */
  showText?: boolean
  /** Custom text to display instead of percentage */
  customText?: string
  /** Additional CSS class name */
  className?: string
  /** Test ID for testing purposes */
  testId?: string
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 44,
  strokeWidth = 8.5,
  progressColor = "#0078d4",
  backgroundColor = "rgba(32, 112, 207, 0.5)",
  innerBackgroundColor = "#212121",
  textColor = "#ffffff",
  fontSize = 11,
  showText = true,
  customText,
  className = "",
  testId
}) => {
  // Ensure value is between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, value))
  
  // Calculate the progress in degrees (360 degrees = 100%)
  const progressDegrees = (clampedValue / 100) * 360
  
  // Calculate inner circle size (outer size minus stroke width * 2)
  const innerSize = size - strokeWidth * 2
  
  const displayText = customText || `${Math.round(clampedValue)}%`

  return (
    <div 
      className={`${styles.circleContainer} ${className}`}
      data-testid={testId}
    >
      <div
        className={styles.circle}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: `conic-gradient(${progressColor} ${progressDegrees}deg, ${backgroundColor} ${progressDegrees}deg)`,
          "--progress": `${progressDegrees}deg`
        } as React.CSSProperties}
      >
        {showText && (
          <div
            className={styles.circleInner}
            style={{
              width: `${innerSize}px`,
              height: `${innerSize}px`,
              backgroundColor: innerBackgroundColor,
              color: textColor,
              fontSize: `${fontSize}px`
            }}
          >
            {displayText}
          </div>
        )}
      </div>
    </div>
  )
}

export default CircularProgress